import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { BackHeader } from "@/components/TopBar";
import { Spinner } from "@/components/common";
import { CompatibilityRing } from "@/components/CompatibilityRing";
import { Button } from "@/components/ui/button";
import { fmtRent, fmtDate } from "@/lib/constants";
import { toast } from "sonner";
import { MapPin, Calendar, Home as HomeIcon, Check, X, MessageCircle } from "lucide-react";

export default function TribeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/tribes/${id}`).then((r) => setT(r.data)).catch(() => toast.error("Tribe not found"));
  useEffect(() => { load(); }, [id]);

  if (!t) return <div><BackHeader title="Tribe" /><Spinner /></div>;

  const spotsLeft = Math.max(0, (t.spots || 0) - (t.members?.length || 0));
  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tribes/${id}/join`);
      toast.success(data.status === "member" ? "You're in!" : "Request sent to the tribe!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not join"); }
    finally { setBusy(false); }
  };
  const accept = async (uid) => {
    try {
      const { data } = await api.post(`/tribes/${id}/requests/${uid}/accept`);
      toast.success("Member added to the tribe!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not accept"); }
  };
  const decline = async (uid) => {
    try { await api.post(`/tribes/${id}/requests/${uid}/decline`); toast("Request declined"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not decline"); }
  };
  const openChat = async () => {
    try { const { data } = await api.get(`/tribes/${id}/chat`); navigate(`/chat/${data.conversation_id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not open chat"); }
  };

  return (
    <div className="pb-28">
      <BackHeader title="Tribe" />
      <div className="space-y-6 px-4 pt-4">
        <div className="rounded-3xl bg-gradient-to-br from-ink to-ink-soft p-6 text-white">
          <span className="rounded-full bg-sun px-3 py-1 text-xs font-bold text-ink">{spotsLeft > 0 ? `Looking for ${spotsLeft} more` : "Tribe full"}</span>
          <h1 className="mt-3 font-heading text-2xl font-extrabold">{t.title}</h1>
          <div className="mt-3 space-y-1.5 text-sm text-white/90">
            <p className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {(t.preferred_localities || []).join(", ")}, {t.city}</p>
            <p className="flex items-center gap-2"><HomeIcon className="h-4 w-4" /> Target: {t.target_config}</p>
            <p className="flex items-center gap-2"><Calendar className="h-4 w-4" /> Move-in {fmtDate(t.move_in_date)}</p>
          </div>
          <p className="mt-3 font-heading text-xl font-extrabold">{fmtRent(t.budget_min)}–{fmtRent(t.budget_max)} <span className="text-sm font-normal text-white/70">per person</span></p>
        </div>

        <div>
          <h3 className="mb-3 font-heading text-lg font-bold text-ink">Current members</h3>
          <div className="space-y-3">
            {(t.members || []).map((m) => (
              <div key={m.user_id} onClick={() => navigate(`/person/${m.user_id}`)} className="flex cursor-pointer items-center gap-3 rounded-3xl bg-white p-3 shadow-soft">
                <img src={resolveImg(m.profile?.photo_url)} alt="" className="h-14 w-14 rounded-2xl object-cover" />
                <div className="flex-1">
                  <h4 className="font-heading font-bold text-ink">{m.profile?.first_name}{m.profile?.age ? `, ${m.profile.age}` : ""} {m.user_id === t.creator_id && <span className="ml-1 rounded-full bg-brand-light px-2 py-0.5 text-[10px] font-bold text-brand-dark">Host</span>}</h4>
                  <p className="text-xs text-ink-soft">{m.profile?.occupation}</p>
                </div>
                <CompatibilityRing score={m.compatibility?.score} size={44} stroke={4} />
              </div>
            ))}
          </div>
        </div>

        {t.is_host && t.pending?.length > 0 && (
          <div>
            <h3 className="mb-3 font-heading text-lg font-bold text-ink">Join requests <span className="rounded-full bg-brand px-2 py-0.5 text-xs text-white">{t.pending.length}</span></h3>
            <div className="space-y-3">
              {t.pending.map((m) => (
                <div key={m.user_id} className="flex items-center gap-3 rounded-3xl bg-white p-3 shadow-soft" data-testid={`pending-${m.user_id}`}>
                  <img onClick={() => navigate(`/person/${m.user_id}`)} src={resolveImg(m.profile?.photo_url)} alt="" className="h-12 w-12 cursor-pointer rounded-2xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <h4 className="font-heading font-bold text-ink">{m.profile?.first_name}{m.profile?.age ? `, ${m.profile.age}` : ""}</h4>
                    <p className="truncate text-xs text-ink-soft">{m.profile?.occupation} · {m.compatibility?.score}% match</p>
                  </div>
                  <button data-testid={`accept-${m.user_id}`} onClick={() => accept(m.user_id)} className="grid h-9 w-9 place-items-center rounded-full bg-mint/15 text-mint"><Check className="h-4 w-4" /></button>
                  <button data-testid={`decline-${m.user_id}`} onClick={() => decline(m.user_id)} className="grid h-9 w-9 place-items-center rounded-full bg-destructive/10 text-destructive"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-1/2 flex w-full max-w-[480px] -translate-x-1/2 gap-3 border-t border-gray-100 bg-white/95 px-4 py-4 backdrop-blur-xl">
        {t.is_member ? (
          <Button data-testid="tribe-group-chat" onClick={openChat} className="h-12 w-full rounded-full bg-brand font-bold text-white shadow-brand hover:bg-brand-dark"><MessageCircle className="mr-1.5 h-4 w-4" /> Open group chat</Button>
        ) : t.is_pending ? (
          <Button disabled variant="outline" className="h-12 w-full rounded-full border-border font-bold">Request pending</Button>
        ) : (
          <Button data-testid="join-tribe" onClick={join} disabled={busy || spotsLeft === 0} className="h-12 w-full rounded-full bg-brand font-bold text-white shadow-brand hover:bg-brand-dark">{spotsLeft === 0 ? "Tribe full" : "Request to join"}</Button>
        )}
      </div>
    </div>
  );
}
